import { Button } from "@d12labs/ui/button";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Pill } from "@d12labs/ui/pill";
import { Textarea } from "@d12labs/ui/textarea";
import { cn } from "@d12labs/utils";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
	ArrowRight,
	Calendar,
	Download,
	Eye,
	Link2,
	Plus,
	Quote,
	Search,
	Sparkles,
	Upload,
	Users,
	X,
	Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { meetingPerfApi } from "../api";
import { Avatar } from "../components/avatar";
import { RemovedMeetings } from "../components/removed-meetings";
import { RoleBadge } from "../components/role-badge";
import { type ParsedSegment, parseLabeledTranscript } from "../parse-transcript";

type Mode = "auto" | "speaker" | "fireflies";

const formatBrowseDate = (iso: string | null): string =>
	iso
		? new Date(iso).toLocaleDateString(undefined, {
				month: "short",
				day: "numeric",
				year: "numeric",
			})
		: "—";

type RoleFilter = "all" | "organizer" | "participant";

type SpeakerDraft = { name: string; isPrimary: boolean; transcript: string };
type Segment = { text: string; startTime?: string | null };

type CreatePayload = {
	title: string;
	source: "manual";
	speakers: { name: string; isPrimary: boolean; segments: Segment[] }[];
};

const emptySpeaker = (): SpeakerDraft => ({ name: "", isPrimary: false, transcript: "" });

// Each non-empty line of a speaker's transcript becomes one segment.
const toSegments = (transcript: string): Segment[] =>
	transcript
		.split("\n")
		.map((l) => l.trim())
		.filter(Boolean)
		.map((text) => ({ text }));

const wordCount = (segments: ParsedSegment[]): number =>
	segments
		.map((s) => s.text)
		.join(" ")
		.split(/\s+/)
		.filter(Boolean).length;

const SAMPLE_TRANSCRIPT = `[00:01] Ada Lovelace: Thanks everyone for jumping on. I want to keep this tight — three things: the migration timeline, the rollback plan, and what we owe the board before Friday.
[00:14] Alan Turing: Before terms, I want to flag the auth migration. Where are we on the dual-write window?
[00:28] Grace Hopper: Phase one ships Tuesday. The dual-write window is six weeks; cutover lands the 18th. I'll send the runbook today.
[00:41] Ada Lovelace: And we'll cover migration support hours at no charge — that's already in the redline.
[00:52] Alan Turing: That helps. Walk me through the rollback story, because that's the first question I'll get.
[01:05] Grace Hopper: Every phase is reversible inside the window. If cutover regresses, we fail back to the old path in under five minutes.`;

export function UploadPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const search = useSearch({ strict: false }) as { source?: "fireflies" };
	const [title, setTitle] = useState("");
	const [mode, setMode] = useState<Mode>(search.source === "fireflies" ? "fireflies" : "auto");

	// Auto-split mode: one labeled transcript, parsed into speakers.
	const [raw, setRaw] = useState("");
	const [autoPrimary, setAutoPrimary] = useState("");
	const parsed = useMemo(() => parseLabeledTranscript(raw), [raw]);

	// By-speaker mode: one card per speaker.
	const [speakers, setSpeakers] = useState<SpeakerDraft[]>([emptySpeaker()]);

	// Fireflies import (relocated from the dashboard).
	const [fireflyId, setFireflyId] = useState("");
	const firefliesConfig = useQuery({
		queryKey: ["meeting-perf", "import-config"],
		queryFn: () => meetingPerfApi.firefliesStatus(),
	});

	// Picker state: role filter + title search + sort, paginated 20 at a time.
	const PAGE = 20;
	const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
	const [browseSearch, setBrowseSearch] = useState("");
	const [sortNewest, setSortNewest] = useState(true);
	const [visibleCount, setVisibleCount] = useState(PAGE);
	const [manualOpen, setManualOpen] = useState(false);

	// Search runs in Fireflies (whole workspace, by title), so debounce the box
	// rather than firing a request per keystroke.
	const [debouncedSearch, setDebouncedSearch] = useState("");
	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(browseSearch.trim()), 300);
		return () => clearTimeout(t);
	}, [browseSearch]);

	// Fireflies browser — meetings NOT yet imported (D12-60). Live view; rows
	// disappear on refetch once their import lands. Each page scans the next
	// batch of Fireflies transcripts; "Load older" follows `nextSkip`, so there
	// is no overall cap — any meeting is reachable by scrolling or searching.
	const browse = useInfiniteQuery({
		queryKey: ["meeting-perf", "fireflies-browse", debouncedSearch],
		queryFn: ({ pageParam }) =>
			meetingPerfApi.firefliesBrowse({
				query: { skip: pageParam, q: debouncedSearch || undefined },
			}),
		initialPageParam: 0,
		getNextPageParam: (last) => last.nextSkip ?? undefined,
		enabled: mode === "fireflies" && firefliesConfig.data?.configured === true,
	});
	// Locally-queued rows: Process disabled until the refetch drops the row.
	const [queuedIds, setQueuedIds] = useState<Set<string>>(new Set());

	// Flatten the pages; de-dupe by id because offset paging can shift by a row
	// when a new recording lands between "Load older" clicks.
	const browseItems = useMemo(() => {
		const seen = new Set<string>();
		return (browse.data?.pages ?? [])
			.flatMap((p) => p.meetings)
			.filter((m) => {
				if (seen.has(m.id)) return false;
				seen.add(m.id);
				return true;
			});
	}, [browse.data]);
	const roleCounts: Record<RoleFilter, number> = {
		all: browseItems.length,
		organizer: browseItems.filter((m) => m.involvement === "organizer").length,
		participant: browseItems.filter((m) => m.involvement === "participant").length,
	};
	const browseSearchLower = debouncedSearch.toLowerCase();
	const filteredBrowse = browseItems.filter(
		(m) => roleFilter === "all" || m.involvement === roleFilter,
	);
	const loadOlder = () => {
		setVisibleCount((c) => c + PAGE);
		void browse.fetchNextPage();
	};
	// The endpoint returns newest-first; "oldest first" is just the reverse.
	const orderedBrowse = sortNewest ? filteredBrowse : [...filteredBrowse].reverse();
	const visibleBrowse = orderedBrowse.slice(0, visibleCount);

	// "Removed" — Fireflies meetings the user deleted, which imports now skip.
	const [showRemoved, setShowRemoved] = useState(false);
	const removed = useQuery({
		queryKey: ["meeting-perf", "fireflies-removed"],
		queryFn: () => meetingPerfApi.firefliesRemoved(),
		enabled: mode === "fireflies" && firefliesConfig.data?.configured === true,
	});
	const removedItems = removed.data?.items ?? [];

	const pickRole = (r: RoleFilter) => {
		setShowRemoved(false);
		setRoleFilter(r);
		setVisibleCount(PAGE);
	};
	const pickSearch = (value: string) => {
		setBrowseSearch(value);
		setVisibleCount(PAGE);
	};
	const processMeeting = useMutation({
		mutationFn: (firefliesMeetingId: string) =>
			meetingPerfApi.firefliesImport({ input: { firefliesMeetingId } }),
		onMutate: (id) => setQueuedIds((prev) => new Set(prev).add(id)),
		onSuccess: () => {
			toast.success("Processing queued — the meeting will appear once it's analyzed");
			queryClient.invalidateQueries({ queryKey: ["meeting-perf"] });
		},
		onError: (err, id) => {
			setQueuedIds((prev) => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
			toast.error((err as Error).message);
		},
	});

	const create = useMutation({
		mutationFn: (payload: CreatePayload) => meetingPerfApi.createMeeting({ input: payload }),
		onSuccess: ({ meeting }) => {
			queryClient.invalidateQueries({ queryKey: ["meeting-perf"] });
			toast.success("Meeting created");
			navigate({ to: "/meeting-perf/meetings/$id", params: { id: meeting.id } });
		},
		onError: (err) => toast.error((err as Error).message),
	});

	const importMeeting = useMutation({
		mutationFn: (firefliesMeetingId: string) =>
			meetingPerfApi.firefliesImport({ input: { firefliesMeetingId } }),
		onSuccess: () => {
			toast.success("Import queued — the meeting will appear once it's processed");
			setFireflyId("");
			queryClient.invalidateQueries({ queryKey: ["meeting-perf"] });
			navigate({ to: "/meeting-perf" });
		},
		onError: (err) => toast.error((err as Error).message),
	});

	const updateSpeaker = (i: number, patch: Partial<SpeakerDraft>) =>
		setSpeakers((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
	const setPrimary = (i: number) =>
		setSpeakers((prev) => prev.map((s, idx) => ({ ...s, isPrimary: idx === i })));

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (title.trim().length === 0) {
			toast.error("Give the meeting a title");
			return;
		}
		const prepared: CreatePayload["speakers"] =
			mode === "auto"
				? parsed.map((p) => ({
						name: p.name,
						isPrimary: p.name === autoPrimary,
						segments: p.segments,
					}))
				: speakers
						.map((s) => ({
							name: s.name.trim(),
							isPrimary: s.isPrimary,
							segments: toSegments(s.transcript),
						}))
						.filter((s) => s.name.length > 0 && s.segments.length > 0);

		if (prepared.length === 0) {
			toast.error(
				mode === "auto"
					? "Paste a transcript with 'Name:' lines"
					: "Add at least one speaker with transcript lines",
			);
			return;
		}
		create.mutate({ title: title.trim(), source: "manual", speakers: prepared });
	};

	const useSample = () => {
		setRaw(SAMPLE_TRANSCRIPT);
		if (!title) setTitle("Migration planning sync");
	};

	const tabs: { id: Mode; label: string; icon: typeof Quote }[] = [
		{ id: "auto", label: "Paste transcript", icon: Quote },
		{ id: "speaker", label: "By speaker", icon: Users },
		{ id: "fireflies", label: "Import from Fireflies", icon: Download },
	];

	return (
		<>
			<PageHeader
				eyebrow="New meeting"
				icon={Upload}
				title="Analyze a conversation"
				subtitle="Paste a transcript and we'll split it by speaker, score each across six KPIs, and roll the results into every tracked speaker's trend."
				actions={
					<>
						<Link to="/meeting-perf">
							<Button type="button" variant="ghost" size="sm">
								<X className="h-3.5 w-3.5" /> Cancel
							</Button>
						</Link>
						{mode !== "fireflies" ? (
							<Button type="submit" form="upload-form" disabled={create.isPending} size="sm">
								{create.isPending ? (
									"Creating…"
								) : (
									<>
										Create meeting <ArrowRight className="h-3.5 w-3.5" />
									</>
								)}
							</Button>
						) : null}
					</>
				}
			/>
			<PageBody className="p-6">
				<form
					id="upload-form"
					onSubmit={handleSubmit}
					className="mx-auto flex w-full max-w-5xl flex-col gap-6"
				>
					{/* Source tabs */}
					<div className="flex gap-1 border-b border-hairline">
						{tabs.map((t) => {
							const Icon = t.icon;
							const active = mode === t.id;
							return (
								<button
									key={t.id}
									type="button"
									onClick={() => setMode(t.id)}
									className={cn(
										"-mb-px flex items-center gap-2 px-3.5 py-2.5 text-body-sm font-medium transition-colors",
										active
											? "border-b-2 border-foreground text-foreground"
											: "border-b-2 border-transparent text-ink-subtle hover:text-foreground",
									)}
								>
									<Icon className="h-3.5 w-3.5" /> {t.label}
								</button>
							);
						})}
					</div>

					{/* Title (paste + by-speaker) */}
					{mode !== "fireflies" ? (
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="title">Title</Label>
							<Input
								id="title"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								placeholder="e.g. Acme — Quarterly Business Review"
								className="max-w-lg"
								required
							/>
						</div>
					) : null}

					{mode === "auto" ? (
						<div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
							{/* Transcript paste */}
							<div className="flex flex-col gap-2">
								<div className="flex items-baseline justify-between">
									<Label htmlFor="raw">Transcript</Label>
									<Button type="button" variant="ghost" size="sm" onClick={useSample}>
										<Sparkles className="h-3.5 w-3.5" /> Use sample
									</Button>
								</div>
								<Textarea
									id="raw"
									value={raw}
									onChange={(e) => setRaw(e.target.value)}
									rows={18}
									className="min-h-[360px] font-mono text-xs leading-relaxed"
									placeholder={
										"[00:01] Ada Lovelace: Thanks everyone for joining.\nAlan Turing: Quick question on scope.\nAda Lovelace: Good point — let's dig in."
									}
								/>
								<div className="flex flex-wrap items-center gap-3 text-xs text-ink-tertiary">
									<span>
										One line per remark, prefixed with{" "}
										<code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono">Name:</code>
									</span>
									<span>·</span>
									<span className="tabular-nums">{raw.length.toLocaleString()} chars</span>
								</div>
							</div>

							{/* Detected speakers preview */}
							<div className="flex flex-col gap-3 lg:sticky lg:top-6 lg:self-start">
								<div className="rounded-lg border border-hairline bg-surface-1 p-5">
									<div className="mb-3 flex items-center justify-between">
										<span className="text-body-sm font-medium text-ink">Detected speakers</span>
										<span className="text-eyebrow uppercase tabular-nums text-ink-subtle">
											{parsed.length} found
										</span>
									</div>
									{parsed.length === 0 ? (
										<div className="flex flex-col items-center justify-center gap-2.5 rounded-md border border-dashed border-hairline-strong px-4 py-8">
											<Users className="h-5 w-5 text-ink-subtle" />
											<span className="text-center text-meta text-ink-subtle">
												Paste a labeled transcript — we'll list each speaker here and let you pick
												the primary one to track.
											</span>
										</div>
									) : (
										<div className="flex flex-col gap-1.5">
											{parsed.map((p) => {
												const isPrimary = autoPrimary === p.name;
												return (
													<label
														key={p.name}
														className={cn(
															"flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5",
															isPrimary
																? "border-hairline-strong bg-surface-2"
																: "border-transparent hover:bg-surface-2/60",
														)}
													>
														<input
															type="radio"
															name="auto-primary"
															checked={isPrimary}
															onChange={() => setAutoPrimary(p.name)}
															style={{ accentColor: "var(--color-primary)" }}
														/>
														<Avatar name={p.name} />
														<div className="flex min-w-0 flex-1 flex-col">
															<span className="truncate text-body-sm font-medium">{p.name}</span>
															<span className="text-meta text-ink-subtle">
																{p.segments.length} segment{p.segments.length === 1 ? "" : "s"} ·{" "}
																{wordCount(p.segments)} words
															</span>
														</div>
														{isPrimary ? (
															<span className="text-meta font-medium text-primary">Primary</span>
														) : null}
													</label>
												);
											})}
										</div>
									)}
								</div>

								<div className="rounded-lg border border-hairline bg-surface-1 p-4">
									<div className="flex items-start gap-3">
										<Sparkles className="mt-0.5 h-3.5 w-3.5 flex-none text-ink-subtle" />
										<div className="flex flex-col gap-1">
											<span className="text-body-sm font-medium">How analysis works</span>
											<span className="text-meta text-ink-subtle">
												We split your transcript by speaker, then ask Claude to score each on
												Clarity, Conciseness, Confidence, Engagement, Structure, and Professionalism
												— and surface strengths, gaps, and notable quotes.
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					) : null}

					{mode === "speaker" ? (
						<div className="flex flex-col gap-3">
							{speakers.map((s, i) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: speaker rows are positional and reorder-free
								<div key={i} className="rounded-lg border border-hairline bg-surface-1 p-5">
									<div className="mb-3.5 flex items-center justify-between">
										<span className="text-body-sm font-medium text-ink">Speaker {i + 1}</span>
										{speakers.length > 1 ? (
											<Button
												type="button"
												variant="ghost"
												size="sm"
												onClick={() => setSpeakers((prev) => prev.filter((_, idx) => idx !== i))}
											>
												<X className="h-3.5 w-3.5" /> Remove
											</Button>
										) : null}
									</div>
									<div className="mb-3 flex items-end gap-3">
										<div className="flex flex-1 flex-col gap-1.5">
											<Label htmlFor={`name-${i}`}>Name</Label>
											<Input
												id={`name-${i}`}
												value={s.name}
												onChange={(e) => updateSpeaker(i, { name: e.target.value })}
												placeholder="Ada Lovelace"
											/>
										</div>
										<label className="flex items-center gap-2 pb-2 text-body-sm">
											<input
												type="radio"
												name="primary"
												checked={s.isPrimary}
												onChange={() => setPrimary(i)}
												style={{ accentColor: "var(--color-primary)" }}
											/>
											Primary
										</label>
									</div>
									<div className="flex flex-col gap-1.5">
										<Label htmlFor={`transcript-${i}`}>Transcript (one line per remark)</Label>
										<Textarea
											id={`transcript-${i}`}
											value={s.transcript}
											onChange={(e) => updateSpeaker(i, { transcript: e.target.value })}
											rows={5}
											className="font-mono text-xs"
											placeholder={"Thanks everyone for joining.\nLet's start with the roadmap."}
										/>
									</div>
								</div>
							))}
							<Button
								type="button"
								variant="secondary"
								className="self-start"
								onClick={() => setSpeakers((prev) => [...prev, emptySpeaker()])}
							>
								<Plus className="h-4 w-4" /> Add speaker
							</Button>
						</div>
					) : null}

					{mode === "fireflies" ? (
						!firefliesConfig.data?.configured ? (
							<div className="max-w-xl rounded-lg border border-hairline bg-surface-1 p-5">
								<p className="text-body-sm text-ink-subtle">
									The Fireflies integration isn't configured for this deployment. Set{" "}
									<code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs">
										FIREFLIES_API_KEY
									</code>{" "}
									to enable on-demand import, or paste a transcript instead.
								</p>
							</div>
						) : (
							<div className="flex flex-col">
								{/* Toolbar — search + role pills + manual import, all above the fold. */}
								<div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-hairline bg-canvas py-2.5">
									<div className="flex items-center gap-2">
										<div className="relative">
											<Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-tertiary" />
											<Input
												className="h-7 w-52 pl-8 text-body-sm"
												placeholder="Search meetings…"
												value={browseSearch}
												onChange={(e) => pickSearch(e.target.value)}
											/>
										</div>
										<Pill
											active={!showRemoved && roleFilter === "all"}
											onClick={() => pickRole("all")}
										>
											All
										</Pill>
										<Pill
											active={!showRemoved && roleFilter === "organizer"}
											onClick={() => pickRole("organizer")}
										>
											Organizer
										</Pill>
										<Pill
											active={!showRemoved && roleFilter === "participant"}
											onClick={() => pickRole("participant")}
										>
											Participant
										</Pill>
										<span className="mx-1 h-4 w-px bg-hairline" />
										<Pill active={showRemoved} onClick={() => setShowRemoved(true)}>
											Removed
											{removedItems.length > 0 ? (
												<span className="ml-1.5 tabular-nums text-ink-tertiary">
													{removedItems.length}
												</span>
											) : null}
										</Pill>
									</div>
									{manualOpen ? (
										<div className="flex h-8 items-center gap-2 rounded-md border border-primary/40 bg-surface-1 pl-2.5 pr-1">
											<Link2 className="size-3.5 flex-none text-ink-subtle" />
											<input
												// biome-ignore lint/a11y/noAutofocus: the field appears on explicit click — focus belongs in it
												autoFocus
												value={fireflyId}
												onChange={(e) => setFireflyId(e.target.value)}
												placeholder="Fireflies meeting ID — 01J7K…"
												className="w-56 bg-transparent font-mono text-body-sm text-ink outline-none placeholder:text-ink-tertiary"
											/>
											<Button
												type="button"
												size="sm"
												disabled={!fireflyId.trim() || importMeeting.isPending}
												onClick={() => importMeeting.mutate(fireflyId.trim())}
											>
												<Download className="h-3.5 w-3.5" />
												{importMeeting.isPending ? "Importing…" : "Import"}
											</Button>
											<button
												type="button"
												aria-label="Close manual import"
												onClick={() => setManualOpen(false)}
												className="flex size-6 items-center justify-center rounded text-ink-subtle hover:text-ink"
											>
												<X className="size-3.5" />
											</button>
										</div>
									) : (
										<Button
											type="button"
											variant="secondary"
											size="sm"
											onClick={() => setManualOpen(true)}
										>
											<Link2 className="h-3.5 w-3.5" /> Import by ID
										</Button>
									)}
								</div>

								{showRemoved ? (
									<RemovedMeetings
										items={removedItems}
										isLoading={removed.isLoading}
										isError={removed.isError}
									/>
								) : (
									<>
										{/* Context strip — connection status, filtered count, sort. */}
										<div className="flex items-center justify-between py-2">
											<div className="flex items-center gap-2">
												<span className="inline-flex items-center gap-1.5 text-meta font-medium text-semantic-success">
													<span className="size-1.5 rounded-full bg-semantic-success" />
													Workspace connected
												</span>
												<span className="text-meta text-ink-tertiary">·</span>
												<span className="text-meta tabular-nums text-ink-subtle">
													{browse.isLoading
														? "Loading meetings…"
														: `${
																roleFilter === "all"
																	? `${roleCounts.all} un-imported meeting${roleCounts.all === 1 ? "" : "s"}`
																	: `${roleCounts[roleFilter]} where you're ${roleFilter}`
															}${browseSearchLower ? ` matching "${debouncedSearch}"` : ""}${
																browse.hasNextPage ? " loaded — older ones in Fireflies" : ""
															}`}
													{" · "}
													{sortNewest ? "newest first" : "oldest first"}
												</span>
											</div>
											<button
												type="button"
												className="text-meta text-ink-subtle transition-colors hover:text-ink"
												onClick={() => setSortNewest((v) => !v)}
											>
												Sort: {sortNewest ? "Newest" : "Oldest"} ▾
											</button>
										</div>

										{/* Dense paginated rows. */}
										<div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
											{browse.isError ? (
												<p className="px-4 py-6 text-body-sm text-ink-subtle">
													Couldn't reach Fireflies — try again shortly.
												</p>
											) : browse.isLoading ? (
												<p className="px-4 py-6 text-body-sm text-ink-subtle">Loading…</p>
											) : orderedBrowse.length === 0 ? (
												<div className="flex items-center justify-between gap-3 px-4 py-6">
													<p className="text-body-sm text-ink-subtle">
														{browseItems.length > 0
															? "No meetings match this filter."
															: browseSearchLower
																? `No un-imported meetings titled "${debouncedSearch}"${browse.hasNextPage ? " so far" : ""}.`
																: browse.hasNextPage
																	? "Everything recent is already imported."
																	: "Everything in Fireflies is already imported."}
													</p>
													{browse.hasNextPage ? (
														<Button
															type="button"
															variant="secondary"
															size="sm"
															disabled={browse.isFetchingNextPage}
															onClick={loadOlder}
														>
															{browse.isFetchingNextPage ? "Loading…" : "Load older meetings"}
														</Button>
													) : null}
												</div>
											) : (
												<>
													<ul className="divide-y divide-hairline">
														{visibleBrowse.map((m) => {
															const queued = queuedIds.has(m.id);
															return (
																<li
																	key={m.id}
																	className="group grid h-[52px] grid-cols-[1fr_auto] items-center gap-4 px-4 transition-colors hover:bg-surface-2/40"
																>
																	<div className="flex min-w-0 items-center gap-2.5">
																		<span className="flex size-7 flex-none items-center justify-center rounded-md border border-hairline bg-surface-2 text-ink-subtle">
																			<Calendar className="size-3.5" />
																		</span>
																		<div className="min-w-0">
																			<div className="flex items-center gap-2">
																				<span className="truncate text-body-sm font-medium text-ink">
																					{m.title}
																				</span>
																				<RoleBadge role={m.involvement} />
																			</div>
																			<div className="text-meta tabular-nums text-ink-subtle">
																				{formatBrowseDate(m.meetingDate)}
																				{m.durationMinutes != null
																					? ` · ${m.durationMinutes}m`
																					: ""}
																			</div>
																		</div>
																	</div>
																	<div className="flex items-center gap-2">
																		<Button
																			type="button"
																			variant="ghost"
																			size="sm"
																			className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
																			onClick={() =>
																				window.open(
																					`https://app.fireflies.ai/view/${encodeURIComponent(m.id)}`,
																					"_blank",
																					"noopener",
																				)
																			}
																		>
																			<Eye className="h-3.5 w-3.5" /> Preview
																		</Button>
																		<Button
																			type="button"
																			size="sm"
																			disabled={queued || processMeeting.isPending}
																			onClick={() => processMeeting.mutate(m.id)}
																		>
																			<Zap className="h-3.5 w-3.5" />
																			{queued ? "Queued" : "Process"}
																		</Button>
																	</div>
																</li>
															);
														})}
													</ul>
													<div className="flex items-center justify-center gap-3 border-t border-hairline px-4 py-3">
														<span className="text-meta tabular-nums text-ink-subtle">
															Showing {visibleBrowse.length} of {orderedBrowse.length}
														</span>
														{visibleCount < orderedBrowse.length ? (
															<Button
																type="button"
																variant="secondary"
																size="sm"
																onClick={() => setVisibleCount((c) => c + PAGE)}
															>
																Load {Math.min(PAGE, orderedBrowse.length - visibleCount)} more
															</Button>
														) : browse.hasNextPage ? (
															<Button
																type="button"
																variant="secondary"
																size="sm"
																disabled={browse.isFetchingNextPage}
																onClick={loadOlder}
															>
																{browse.isFetchingNextPage ? "Loading…" : "Load older meetings"}
															</Button>
														) : null}
													</div>
												</>
											)}
										</div>
									</>
								)}
							</div>
						)
					) : null}

					{/* Footer action bar (paste + by-speaker) */}
					{mode !== "fireflies" ? (
						<div className="flex items-center justify-between gap-4 border-t border-hairline pt-4">
							<span className="text-meta text-ink-subtle">
								{mode === "auto"
									? parsed.length === 0
										? "Paste a transcript to enable analysis."
										: `Ready: ${parsed.length} speaker${parsed.length === 1 ? "" : "s"} detected${autoPrimary ? `, primary: ${autoPrimary}` : ""}.`
									: "Fill in at least one speaker to enable analysis."}
							</span>
							<div className="flex gap-2">
								<Link to="/meeting-perf">
									<Button type="button" variant="ghost">
										Cancel
									</Button>
								</Link>
								<Button type="submit" disabled={create.isPending}>
									{create.isPending ? (
										"Creating…"
									) : (
										<>
											Create meeting <ArrowRight className="h-3.5 w-3.5" />
										</>
									)}
								</Button>
							</div>
						</div>
					) : null}
				</form>
			</PageBody>
		</>
	);
}
